import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  ChatsCircle, ChatCircleDots, Plus, CheckCircle, WarningCircle, Trash, PaperPlaneTilt, ArrowCounterClockwise, CaretDown, CaretRight, FunnelSimple, Eye, Check, Paperclip,
} from "@phosphor-icons/react";
import EntityHistory from "../components/EntityHistory";
import FlowStrip from "../components/FlowStrip";
import PageHelpButton from "../components/PageHelpButton";
import DocumentViewer from "../components/DocumentViewer";
import DirectedToPicker from "../components/DirectedToPicker";

// Module-level static so the array isn't reallocated each render.
const INQUIRIES_FLOW_STEPS = [
  { icon: ChatsCircle,    title: "Inquiry comes in",     sub: "Bookkeeper asks about a check, charge, or doc.",  accent: "#002FA7" },
  { icon: PaperPlaneTilt, title: "You answer or comment", sub: "Submit response = resolved · comment = keep talking.", accent: "#7C3AED" },
  { icon: CheckCircle,    title: "Resolved",              sub: "Archived in the Resolved tab — reopen anytime.",  accent: "#10B981" },
];

export default function Questions() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const [tab, setTab] = useState("unresolved");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  // Default month_about = the closing (previous) calendar month — matches backend default
  const defaultMonthAbout = (() => {
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [monthAbout, setMonthAbout] = useState(defaultMonthAbout);
  // Smart routing default — opposite of the creator's role; staff defaults to bookkeeper.
  const defaultDirectedTo =
    user?.role === "owner" ? "bookkeeper" :
    user?.role === "bookkeeper" ? "owner" :
    "bookkeeper";
  const [directedTo, setDirectedTo] = useState(defaultDirectedTo);
  // Routing filter (page-local, defaults to "all" — never persisted)
  const [routingFilter, setRoutingFilter] = useState("all");
  const [groupByMonth, setGroupByMonth] = useState(false);
  const [replies, setReplies] = useState({});
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(false);
  // Linked-document viewer — opened when a responder taps "View document" on an inquiry card.
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => { setDirectedTo(defaultDirectedTo); }, [defaultDirectedTo]);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/questions?company_id=${activeCompanyId}`);
    setItems(data);
  }, [activeCompanyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((q) => {
    if (q.status !== tab) return false;
    if (routingFilter === "all") return true;
    if (routingFilter === "for_me") return q.directed_to === user?.role;
    return q.directed_to === routingFilter;
  });

  const handleCreate = async () => {
    if (!title.trim()) return;
    setLoading(true);
    try {
      await api.post("/questions", {
        company_id: activeCompanyId,
        title: title.trim(),
        body: body.trim() || null,
        due_date: dueDate || null,
        month_about: monthAbout || null,
        directed_to: directedTo || null,
      });
      toast.success("Inquiry submitted");
      setTitle("");
      setBody("");
      setDueDate("");
      setMonthAbout(defaultMonthAbout);
      setDirectedTo(defaultDirectedTo);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error("Failed to submit inquiry");
    }
    setLoading(false);
  };

  const handleComment = async (qid) => {
    const text = comments[qid];
    if (!text?.trim()) return;
    try {
      await api.post(`/questions/${qid}/comment`, { body: text.trim() });
      setComments((c) => ({ ...c, [qid]: "" }));
      await load();
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  // Attach files to an inquiry. Uploaded with question_id so they show up
  // both on the inquiry card AND on the Files page (they share storage).
  const handleAttachFiles = async (qid, files) => {
    if (!files || !files.length) return;
    let okCount = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("company_id", activeCompanyId);
        fd.append("category", "question_attachment");
        fd.append("question_id", qid);
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (okCount > 0) {
      toast.success(`Attached ${okCount} file${okCount === 1 ? "" : "s"}`);
      await load();
    }
  };

  const handleViewAttachment = (att) => {
    // The Inquiries page reuses Files-page document viewer. We pop open the
    // document by routing through the global DocumentViewer if available; for
    // now, open in a new tab via the documents/<id>/download endpoint.
    setViewingDoc({
      id: att.id,
      display_filename: att.display_filename,
      original_filename: att.display_filename,
      content_type: att.content_type,
    });
  };

  const handleReply = async (qid) => {
    const answer = replies[qid];
    if (!answer?.trim()) return;
    try {
      await api.post(`/questions/${qid}/reply`, { answer_body: answer.trim() });
      toast.success("Replied & resolved");
      setReplies((r) => ({ ...r, [qid]: "" }));
      await load();
    } catch (e) {
      toast.error("Failed to reply");
    }
  };

  // "Mark resolved" — owner handled the inquiry out-of-band and doesn't want
  // to type a written response. One-click resolve, no comment required.
  const handleMarkResolved = async (qid) => {
    if (!confirm("Mark this inquiry resolved without a written reply?")) return;
    try {
      await api.post(`/questions/${qid}/reply`, { answer_body: null });
      toast.success("Marked resolved");
      setReplies((r) => ({ ...r, [qid]: "" }));
      await load();
    } catch (e) {
      toast.error("Failed to mark resolved");
    }
  };

  const handleDelete = async (qid) => {
    if (!confirm("Delete this question?")) return;
    try {
      await api.delete(`/questions/${qid}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleReopen = async (qid) => {
    try {
      await api.post(`/questions/${qid}/reopen`);
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="questions-page" className="p-6 sm:p-8 max-w-[1200px] pb-24 sm:pb-8">
      <div className="mb-8 flex flex-col gap-4">
        <div>
          <div className="label-tag mb-2 flex items-center gap-2">
            <span>Inquiries</span>
            <PageHelpButton
              testId="inquiries-help"
              title="How Inquiries work"
              bullets={[
                <>Use this instead of email for any question about a specific charge, check, or document — e.g., <i>&ldquo;Who is check #1242 made out to?&rdquo;</i> or <i>&ldquo;Which invoice does this $4,200 deposit belong to?&rdquo;</i></>,
                <>Bookkeeper clicks <b>New inquiry</b> → owner gets a notification → owner types a reply in <b>Submit response</b> (which auto-marks it <b>Resolved</b>).</>,
                <>Add a <b>Needed by</b> date so the owner sees the deadline — overdue inquiries get a red pill on the row.</>,
                <>Resolved inquiries archive in the <b>Resolved</b> tab. Hit <b>Reopen</b> if you need to revisit one.</>,
              ]}
              footer={<>Tip: post the inquiry with row info (date + amount) so the owner can find the check or remittance instantly.</>}
            />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
            Open inquiries.
          </h1>
          <p className="text-[#525252] mt-3 max-w-xl">
            {activeCompany?.name}. Items awaiting clarification from the owner, and resolved records for reference.
          </p>
          <div className="mt-4 max-w-3xl">
            <FlowStrip
              storageKey="flow-inquiries-v1"
              title="The inquiry flow at a glance"
              testId="inquiries-flow"
              steps={INQUIRIES_FLOW_STEPS}
              footer={<>The sidebar &ldquo;Inquiries&rdquo; link turns <b className="text-[#DC2626]">red</b> with a count whenever there are open items waiting for your response.</>}
            />
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-ikb px-4 py-3 flex items-center justify-center gap-2 w-full sm:w-auto sm:self-start" data-testid="new-question-button">
              <Plus size={16} weight="bold" />
              New inquiry
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">New inquiry</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="label-tag block mb-2">Subject</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Payee for check #1242"
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                  data-testid="new-question-title"
                />
              </div>
              <div>
                <label className="label-tag block mb-2">Details (optional)</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  placeholder="Provide context, amount, date, or any specifics…"
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none resize-none"
                  data-testid="new-question-body"
                />
              </div>
              <div>
                <label className="label-tag block mb-2">Needed by (optional)</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                  data-testid="new-question-due-date"
                />
              </div>
              <div>
                <label className="label-tag block mb-2">Which month is this about?</label>
                <input
                  type="month"
                  value={monthAbout}
                  onChange={(e) => setMonthAbout(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                  data-testid="new-question-month-about"
                />
                <div className="text-[11px] text-[#525252] mt-1">
                  Defaults to the closing month — change it if you&rsquo;re asking about a different period.
                </div>
              </div>
              <div>
                <label className="label-tag block mb-2">Who&rsquo;s this for?</label>
                <DirectedToPicker
                  value={directedTo}
                  onChange={setDirectedTo}
                  testid="new-question-directed"
                />
                <div className="text-[11px] text-[#525252] mt-1">
                  Only that person gets the email/in-app notification.
                </div>
              </div>
            </div>
            <DialogFooter>
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all"
              >Cancel</button>
              <button
                onClick={handleCreate}
                disabled={loading || !title.trim()}
                className="btn-ikb px-4 py-2"
                data-testid="submit-new-question"
              >
                {loading ? "Submitting…" : "Submit inquiry"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <InquiryAgingPanel companyId={activeCompanyId} />

      {/* Routing filter — page-local, defaults to "All" every visit */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="questions-routing-filter">
        <FunnelSimple size={12} weight="duotone" className="text-[#525252] mr-0.5" />
        {[
          { key: "all", label: "All", color: "#0A0A0A", count: items.filter((q) => q.status === tab).length },
          { key: "for_me", label: "For me", color: "#0A0A0A", count: items.filter((q) => q.status === tab && q.directed_to === user?.role).length },
          { key: "bookkeeper", label: "Bookkeeper", color: "#10B981", count: items.filter((q) => q.status === tab && q.directed_to === "bookkeeper").length },
          { key: "owner", label: "Owner", color: "#002FA7", count: items.filter((q) => q.status === tab && q.directed_to === "owner").length },
          { key: "staff", label: "Admin", color: "#7C3AED", count: items.filter((q) => q.status === tab && q.directed_to === "staff").length },
        ].map((b) => {
          const active = routingFilter === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => setRoutingFilter(b.key)}
              className="text-[11px] px-2 py-1 border transition-all flex items-center gap-1.5"
              style={{
                color: active ? "#FFFFFF" : b.color,
                backgroundColor: active ? b.color : "#FFFFFF",
                borderColor: b.color,
              }}
              data-testid={`routing-filter-${b.key}`}
            >
              <span className="font-semibold tracking-wider uppercase">{b.label}</span>
              <span className={`text-[10px] ${active ? "opacity-90" : "opacity-70"}`}>{b.count}</span>
            </button>
          );
        })}
        {routingFilter !== "all" && (
          <button
            type="button"
            onClick={() => setRoutingFilter("all")}
            className="text-[10px] underline ml-2 text-[#525252] hover:text-[#0A0A0A]"
            data-testid="routing-filter-show-all"
          >
            Show all
          </button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="questions-tabs">
        <TabsList className="bg-transparent rounded-none border-b border-[#E5E5E5] w-full justify-start h-auto p-0 mb-6">
          <TabsTrigger
            value="unresolved"
            data-testid="tab-unresolved"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent font-heading px-4 py-2.5"
          >
            Unresolved ({items.filter((q) => q.status === "unresolved").length})
          </TabsTrigger>
          <TabsTrigger
            value="resolved"
            data-testid="tab-resolved"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#10B981] data-[state=active]:text-[#10B981] data-[state=active]:shadow-none data-[state=active]:bg-transparent font-heading px-4 py-2.5"
          >
            Resolved ({items.filter((q) => q.status === "resolved").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-0 space-y-4">
          {filtered.length === 0 && (
            <div className="surface-card bg-white p-10 text-center">
              <ChatsCircle size={36} weight="duotone" className="mx-auto text-[#525252] mb-3" />
              <div className="font-heading text-lg">
                {tab === "unresolved" ? "No open inquiries." : "No resolved inquiries yet."}
              </div>
              <div className="text-sm text-[#525252] mt-1">
                {tab === "unresolved"
                  ? "When the bookkeeper submits an inquiry, it will appear here."
                  : "Resolved inquiries and their responses will be archived here."}
              </div>
            </div>
          )}
          {filtered.map((q) => (
            <div
              key={q.id}
              data-testid={`question-${q.id}`}
              className={`surface-card bg-white ${q.status === "unresolved" ? "border-l-2 border-l-[#EF4444]" : "border-l-2 border-l-[#10B981]"}`}
            >
              <div className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  {q.status === "unresolved" ? (
                    <WarningCircle size={20} weight="duotone" className="text-[#EF4444] flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle size={20} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-medium tracking-tight leading-snug">{q.title}</h3>
                    {q.body && <p className="text-sm text-[#525252] mt-2 whitespace-pre-wrap">{q.body}</p>}
                    {q.linked_document && (
                      <button
                        onClick={() => setViewingDoc(q.linked_document)}
                        className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold hover:bg-[#002FA7] hover:text-white transition-colors border border-[#002FA7]/30"
                        title="Open the document this inquiry is about"
                        data-testid={`q-view-doc-${q.id}`}
                      >
                        <Eye size={13} weight="duotone" />
                        View document
                        <span className="font-mono font-normal opacity-80 max-w-[180px] truncate">
                          · {q.linked_document.display_filename || q.linked_document.original_filename}
                        </span>
                      </button>
                    )}
                    <div className="label-tag mt-3 text-[10px] flex items-center gap-2 flex-wrap">
                      <span>submitted by {q.created_by_name} · {new Date(q.created_at).toLocaleString()}</span>
                      {q.directed_to && (() => {
                        const palette = {
                          bookkeeper: { label: "Bookkeeper", color: "#10B981" },
                          owner:      { label: "Owner",      color: "#002FA7" },
                          staff:      { label: "Admin",      color: "#7C3AED" },
                        }[q.directed_to];
                        if (!palette) return null;
                        const forMe = q.directed_to === user?.role;
                        return (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase"
                            style={{
                              color: palette.color,
                              backgroundColor: palette.color + "1A",
                              border: forMe ? `1px solid ${palette.color}` : "1px solid transparent",
                            }}
                            title={forMe ? "Directed to you — please respond" : `Directed to the ${palette.label.toLowerCase()}`}
                            data-testid={`q-directed-${q.id}`}
                          >
                            {forMe ? "FOR YOU" : `For: ${palette.label}`}
                          </span>
                        );
                      })()}
                      {q.month_about && (() => {
                        const [yr, mo] = q.month_about.split("-").map(Number);
                        const today = new Date();
                        const closingDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        const closingKey = `${closingDate.getFullYear()}-${String(closingDate.getMonth() + 1).padStart(2, "0")}`;
                        const isOlder = q.month_about < closingKey;
                        const label = new Date(yr, mo - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase ${
                              isOlder && q.status === "unresolved"
                                ? "bg-[#EF4444]/10 text-[#EF4444]"
                                : "bg-[#002FA7]/10 text-[#002FA7]"
                            }`}
                            title={isOlder ? "From a prior close period — don't let it slip" : "Close-period this is about"}
                            data-testid={`q-month-${q.id}`}
                          >
                            About: {label}{isOlder && q.status === "unresolved" ? " · older" : ""}
                          </span>
                        );
                      })()}
                      {q.due_date && (() => {
                        const due = new Date(q.due_date + "T00:00:00");
                        const now = new Date();
                        const diffDays = Math.floor((due - new Date(now.toDateString())) / 86400000);
                        const overdue = diffDays < 0 && q.status === "unresolved";
                        const soon = diffDays >= 0 && diffDays <= 3 && q.status === "unresolved";
                        return (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase ${
                            overdue ? "bg-[#EF4444]/10 text-[#EF4444]" : soon ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#F5F5F5] text-[#525252]"
                          }`}>
                            {overdue ? "overdue" : "needed by"} {due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(q.id)}
                    className="p-1.5 hover:bg-[#F5F5F5] transition-all text-[#525252]"
                    title="Delete"
                    data-testid={`delete-question-${q.id}`}
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>

                {q.answer_body && (
                  <div className="mt-4 sm:pl-8 border-l-2 border-[#002FA7] bg-[#F5F5F5] p-4">
                    <div className="label-tag text-[10px] mb-2">Response</div>
                    <div className="text-sm whitespace-pre-wrap">{q.answer_body}</div>
                    <div className="label-tag mt-2 text-[10px]">
                      by {q.answered_by_name} · {new Date(q.answered_at).toLocaleString()}
                    </div>
                  </div>
                )}

                {/* Attachments — files uploaded with question_id reference.
                    Shown to both sides of the inquiry. Same files appear on
                    the Files page automatically. */}
                {(q.attachments?.length > 0 || true) && (
                  <div className="mt-4 sm:pl-8" data-testid={`inquiry-attachments-${q.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label-tag text-[10px] flex items-center gap-1.5 text-[#525252]">
                        <Paperclip size={11} weight="duotone" />
                        Attachments
                        {q.attachments?.length > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-[#002FA7] text-white text-[10px] font-bold">
                            {q.attachments.length}
                          </span>
                        )}
                      </div>
                      <label
                        className="text-xs text-[#002FA7] hover:underline cursor-pointer flex items-center gap-1"
                        data-testid={`inquiry-attach-${q.id}`}
                      >
                        <Plus size={12} weight="bold" /> Attach file
                        <input
                          type="file"
                          className="hidden"
                          multiple
                          onChange={(e) => handleAttachFiles(q.id, e.target.files)}
                          accept="image/*,application/pdf,.txt,.eml,.html,.csv,.xls,.xlsx,.doc,.docx"
                          data-testid={`inquiry-attach-input-${q.id}`}
                        />
                      </label>
                    </div>
                    {q.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {q.attachments.map((a) => (
                          <button
                            key={a.id}
                            onClick={() => handleViewAttachment(a)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] bg-white text-xs text-[#171717] hover:border-[#002FA7] hover:text-[#002FA7] transition-all max-w-[260px]"
                            title={`${a.display_filename} · uploaded by ${a.uploaded_by_name}`}
                            data-testid={`inquiry-attachment-${a.id}`}
                          >
                            <Paperclip size={11} weight="duotone" />
                            <span className="truncate">{a.display_filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* CHAT section — back-and-forth comments. Does NOT resolve the inquiry. */}
                <div className="mt-5 sm:pl-8 border-t border-[#E5E5E5] pt-4">
                  <div className="label-tag text-[10px] mb-2 flex items-center gap-1.5">
                    <ChatCircleDots size={11} weight="duotone" className="text-[#525252]" />
                    Chat — back and forth ({(q.comments || []).length})
                    <span className="text-[#A3A3A3] font-normal normal-case tracking-normal">— does NOT resolve the inquiry</span>
                  </div>
                  {q.comments?.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {q.comments.map((c) => (
                        <div key={c.id} className="bg-[#FAFAFA] border-l-2 border-[#E5E5E5] p-3" data-testid={`comment-${c.id}`}>
                          <div className="label-tag text-[10px] mb-1">{c.author_name} · {new Date(c.created_at).toLocaleString()}</div>
                          <div className="text-sm whitespace-pre-wrap">{c.body}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      value={comments[q.id] || ""}
                      onChange={(e) => setComments((c) => ({ ...c, [q.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleComment(q.id); } }}
                      placeholder="Type a quick note for the team…"
                      className="flex-1 px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
                      data-testid={`comment-input-${q.id}`}
                    />
                    <button
                      onClick={() => handleComment(q.id)}
                      disabled={!comments[q.id]?.trim()}
                      className="px-3 py-2 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-sm"
                      data-testid={`comment-submit-${q.id}`}
                    >
                      Send chat
                    </button>
                  </div>
                </div>

                {/* ANSWER section — resolves the inquiry. Owner-facing primary action. */}
                {q.status === "unresolved" && (
                  <div className="mt-5 sm:pl-8 border-t-2 border-[#002FA7] pt-4 bg-[#002FA7]/[0.03] p-3 sm:p-4 sm:pl-8">
                    <div className="label-tag text-[10px] mb-2 flex items-center gap-1.5 text-[#002FA7]">
                      <PaperPlaneTilt size={11} weight="duotone" />
                      Answer — RESOLVES the inquiry
                      <span className="text-[#525252] font-normal normal-case tracking-normal">— this is what closes the question</span>
                    </div>
                    <textarea
                      value={replies[q.id] || ""}
                      onChange={(e) => setReplies((r) => ({ ...r, [q.id]: e.target.value }))}
                      rows={3}
                      placeholder="Your answer to the question — submitting will mark this inquiry resolved."
                      className="w-full px-3 py-2 border border-[#E5E5E5] bg-white focus:border-[#002FA7] focus:outline-none resize-none text-sm"
                      data-testid={`reply-input-${q.id}`}
                    />
                    <div className="flex justify-end mt-2 gap-2 flex-wrap">
                      <button
                        onClick={() => handleMarkResolved(q.id)}
                        className="px-4 py-2.5 text-sm flex items-center gap-1.5 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors font-semibold bg-white"
                        data-testid={`reply-mark-resolved-${q.id}`}
                        title="Resolve without typing a reply (e.g. you handled it offline)"
                      >
                        <Check size={16} weight="bold" />
                        Mark resolved (no text)
                      </button>
                      <button
                        onClick={() => handleReply(q.id)}
                        disabled={!replies[q.id]?.trim()}
                        className="btn-ikb px-4 py-2.5 text-sm flex items-center gap-1.5"
                        data-testid={`reply-submit-${q.id}`}
                      >
                        <PaperPlaneTilt size={16} weight="bold" />
                        Submit answer
                      </button>
                    </div>
                  </div>
                )}

                {q.status === "resolved" && (
                  <div className="mt-4 sm:pl-8">
                    <button
                      onClick={() => handleReopen(q.id)}
                      className="text-xs label-tag text-[#525252] hover:text-[#002FA7] flex items-center gap-1.5"
                      data-testid={`reopen-${q.id}`}
                    >
                      <ArrowCounterClockwise size={12} weight="bold" /> Reopen
                    </button>
                  </div>
                )}

                <details className="mt-4 sm:pl-8 group" data-testid={`question-history-toggle-${q.id}`}>
                  <summary className="text-xs font-medium text-[#525252] hover:text-[#0A0A0A] cursor-pointer select-none flex items-center gap-1">
                    <CaretRight size={10} weight="bold" className="transition-transform group-open:rotate-90" />
                    History
                  </summary>
                  <div className="mt-3">
                    <EntityHistory companyId={activeCompanyId} resourceType="inquiry" resourceId={q.id} compact />
                  </div>
                </details>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <DocumentViewer
        open={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        docs={viewingDoc ? [viewingDoc] : []}
        startId={viewingDoc?.id}
      />

      {/* Mobile-only sticky bottom dock — always within thumb reach so the
          primary action ("New inquiry") doesn't get lost behind the list/tabs.
          Helps any role on a phone, not just the dyslexic-friendly admin. */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] shadow-[0_-2px_8px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-inquiry-dock"
      >
        <div className="flex gap-2 p-3">
          <button
            onClick={() => setOpen(true)}
            className="btn-ikb flex-1 px-3 py-4 flex items-center justify-center gap-2 text-base font-semibold"
            data-testid="mobile-dock-new-inquiry"
            aria-label="Post a new inquiry"
          >
            <Plus size={20} weight="bold" />
            <span>New inquiry</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Per-month aging panel — collapsed by default. Helps spot months where things linger.
function InquiryAgingPanel({ companyId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/inquiries/aging?company_id=${companyId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [companyId]);

  if (!data || data.length === 0) return null;

  const today = new Date();
  const closingDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const closingKey = `${closingDate.getFullYear()}-${String(closingDate.getMonth() + 1).padStart(2, "0")}`;

  const fmt = (m) => {
    if (!m || m === "(untagged)") return "Untagged";
    const [y, mm] = m.split("-").map(Number);
    return new Date(y, mm - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="surface-card bg-white mb-6 group"
      data-testid="inquiry-aging-panel"
    >
      <summary className="px-5 py-3 cursor-pointer flex items-center gap-2 hover:bg-[#FAFAF7]">
        <CaretRight size={12} weight="bold" className="text-[#525252] transition-transform group-open:rotate-90" />
        <div className="label-tag">Aging by close month</div>
        <span className="text-[11px] text-[#525252] ml-2">— spot months where items have lingered</span>
      </summary>
      <div className="px-5 pb-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left label-tag text-[10px] border-b border-[#E5E5E5]">
              <th className="py-2 pr-3">Month</th>
              <th className="py-2 px-3 text-right">Open</th>
              <th className="py-2 px-3 text-right">Resolved</th>
              <th className="py-2 px-3 text-right">Avg days</th>
              <th className="py-2 pl-3 text-right">Slowest</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {data.map((row) => {
              const isOlder = row.month_about !== "(untagged)" && row.month_about < closingKey;
              const flagged = isOlder && row.unresolved > 0;
              return (
                <tr key={row.month_about} className={flagged ? "bg-[#EF4444]/[0.04]" : ""} data-testid={`aging-row-${row.month_about}`}>
                  <td className="py-2 pr-3 font-medium">
                    {fmt(row.month_about)}
                    {flagged && <span className="ml-2 text-[10px] text-[#EF4444]">· older</span>}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{row.unresolved}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-[#525252]">{row.resolved}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-[#525252]">{row.avg_days != null ? `${row.avg_days}d` : "—"}</td>
                  <td className="py-2 pl-3 text-right tabular-nums text-[#525252]">{row.max_days != null ? `${row.max_days}d` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}
